import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Volume2, Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVisiblePolling } from '@/utils/useVisiblePolling';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Number(n || 0).toLocaleString('sv-SE');

export default function QuestionAudioPanel({ token, i18n }) {
  const ar = i18n.language === 'ar';
  const t = (a, s) => (ar ? a : s);
  const [st, setSt] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/tts/status`, { headers: { Authorization: `Bearer ${token}` } });
    setSt(data);
  }, [token]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useVisiblePolling(() => load().catch(() => {}), 10000, !!st?.job?.running);

  const start = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/tts/start`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('بدأ إنشاء الصوت', 'Ljudgenerering startad'));
      setTimeout(() => load().catch(() => {}), 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  if (!st) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const job = st.job || {};
  const reason = job.paused_reason === 'quota_exceeded' ? t('انتهى رصيد ElevenLabs. سيكمل تلقائياً من حيث توقف كل يوم الساعة 03:00 UTC بعد شحن الرصيد، أو اضغط "متابعة" الآن.', 'ElevenLabs-krediterna är slut. Fortsätter automatiskt där det slutade varje dag kl. 03:00 UTC efter påfyllning, eller tryck "Fortsätt" nu.')
    : job.paused_reason === 'invalid_key' ? t('مفتاح ElevenLabs غير صالح.', 'Ogiltig ElevenLabs-nyckel.')
      : job.paused_reason;

  return (
    <div className="space-y-5" dir={ar ? 'rtl' : 'ltr'} data-testid="question-audio-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Volume2 className="w-6 h-6 text-slate-600" />
        <h2 className="text-xl font-bold flex-1">{t('صوت الأسئلة (ElevenLabs)', 'Frågeljud (ElevenLabs)')}</h2>
        <Button onClick={start} disabled={job.running || !st.configured || st.missing === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tts-start-btn">
          {job.running ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Play className="w-4 h-4 me-2" />}
          {st.stored > 0 ? t('متابعة', 'Fortsätt') : t('إنشاء الصوت', 'Skapa ljud')}
        </Button>
      </div>
      <p className="text-sm text-slate-600">{t('القسم المخفي: 9 لغات. التاكسي: السويدية فقط. الصوت يُنشأ مرة واحدة ويُحفظ، والأسئلة الجديدة تحصل على صوتها تلقائياً.', 'Dolda frågor: 9 språk. Taxi: svenska. Ljudet skapas en gång och sparas; nya frågor får ljud automatiskt.')}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[[t('محفوظ', 'Sparat'), st.stored, 'tts-stored'], [t('ناقص', 'Saknas'), st.missing, 'tts-missing'], [t('أحرف ناقصة', 'Tecken kvar'), st.missing_chars, 'tts-missing-chars'], [t('المجموع', 'Totalt'), st.total_items, 'tts-total']].map(([l, v, id]) => (
          <div key={id} className="rounded-xl border bg-white p-3"><p className="text-xs text-slate-500">{l}</p><p className="text-2xl font-bold tabular-nums" data-testid={id}>{fmt(v)}</p></div>
        ))}
      </div>
      {job.running && <p className="text-sm text-emerald-700" data-testid="tts-progress">{t('جارٍ الإنشاء', 'Pågår')}: {fmt(job.done)} / {fmt(job.total)}</p>}
      {reason && <p className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="tts-paused">{reason}</p>}
      {!st.configured && <p className="text-sm text-rose-600">{t('أصوات ElevenLabs متوقفة. الموقع يستخدم الأصوات القديمة.', 'ElevenLabs-ljud är avstängt. Sajten använder de gamla rösterna.')}</p>}
    </div>
  );
}
